import { Download } from "lucide-react";
import { getAffiliateBrandingAssets, useQuery } from "wasp/client/operations";
import { Button } from "../../client/components/ui/button";
import { Card, CardContent } from "../../client/components/ui/card";
import { CardSectionHeader } from "../../client/components/ui/card-section-header";
import useTranslation from "../../client/hooks/useTranslation";
import type { Translations } from "../../client/i18n/translations";

interface BrandingKitSectionProps {
  isDark: boolean;
}

const CATEGORY_ORDER = ["logo", "banner", "screenshot", "guidelines"] as const;

export function assetCategoryLabel(t: Translations, category: string): string {
  switch (category) {
    case "logo":
      return t.affiliate.branding.categoryLogo;
    case "banner":
      return t.affiliate.branding.categoryBanner;
    case "screenshot":
      return t.affiliate.branding.categoryScreenshot;
    case "guidelines":
      return t.affiliate.branding.categoryGuidelines;
    default:
      return category;
  }
}

export default function BrandingKitSection({ isDark }: BrandingKitSectionProps) {
  const t = useTranslation();
  const { data: assets } = useQuery(getAffiliateBrandingAssets);

  const grouped = CATEGORY_ORDER.map((category) => ({
    category,
    items: (assets ?? []).filter((a: any) => a.category === category),
  })).filter((g) => g.items.length > 0);

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.affiliate.branding.title}
        isDark={isDark}
        compact
      />
      <CardContent className="px-4 pb-4 pt-3 flex-1 flex flex-col gap-6">
        {grouped.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            {t.affiliate.branding.empty}
          </p>
        ) : (
          grouped.map((group) => (
            <div key={group.category}>
              <p className="widget-label text-[10px] uppercase tracking-wider mb-2">
                {assetCategoryLabel(t, group.category)}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {group.items.map((asset: any) => (
                  <div
                    key={asset.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2.5"
                  >
                    <span className="text-sm truncate">{asset.name}</span>
                    <Button asChild size="sm" variant="ghost">
                      <a
                        href={asset.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" aria-hidden />
                        {t.affiliate.branding.download}
                      </a>
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}

        {/* Règles de la marque (statique) */}
        <div className="rounded-lg border border-border bg-card p-4">
          <p className="widget-label text-[10px] uppercase tracking-wider mb-3">
            {t.affiliate.branding.rulesTitle}
          </p>
          <p className="text-sm text-muted-foreground mb-3">
            {t.affiliate.branding.rulesLogo}{" "}
            <span className="font-logo font-bold text-foreground">
              edgely<span className="text-[#00C9A7]">.</span>
            </span>
          </p>
          <ul className="space-y-1.5 text-sm text-muted-foreground list-disc pl-5">
            <li>{t.affiliate.branding.ruleNoEarningsPromise}</li>
            <li>{t.affiliate.branding.ruleNoLogoEdit}</li>
            <li>{t.affiliate.branding.ruleColors}</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
